/**
 * 任意テキスト (free_text) コンテンツの音声ファイルアップロードの副作用を担うフック。
 *
 * 分割前は `FreeTextFields` 内のローカル state (`uploading` / `uploadError`) と
 * `handleFileChange` として実装されていたものを、そのまま切り出したもの。
 * 状態遷移 (setUploadError(null) → setUploading(true) → finally setUploading(false)) と
 * エラーメッセージのフォールバック文言は変更していない。
 */
import { useState } from 'react';

export interface UseAudioUploadResult {
  /** アップロード実行中フラグ (ファイル入力の disabled / 「アップロード中...」表示に使う) */
  uploading: boolean;
  /** アップロード失敗時のエラーメッセージ (null = エラーなし) */
  uploadError: string | null;
  /**
   * `<input type="file">` の onChange ハンドラ。
   * 選択されたファイルを `onUploadAudio` へ渡し、成功したら `onUploaded(audioKey)` を呼ぶ。
   */
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
}

export function useAudioUpload({
  onUploadAudio,
  onUploaded,
}: {
  onUploadAudio?: (file: File) => Promise<string>;
  onUploaded: (audioKey: string) => void;
}): UseAudioUploadResult {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    // input を毎回リセットして、同じファイルを連続選択しても onChange が発火するようにする
    e.target.value = '';
    if (!file || !onUploadAudio) return;
    setUploadError(null);
    setUploading(true);
    try {
      const audioKey = await onUploadAudio(file);
      onUploaded(audioKey);
    } catch (err) {
      setUploadError((err as Error).message || '音声ファイルのアップロードに失敗しました');
    } finally {
      setUploading(false);
    }
  };

  return { uploading, uploadError, handleFileChange };
}
